import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import {
  AI_FACTOR_KEYS,
  type AiFactorScores,
} from '../cowbellFactors/aiFactors.model';
import {
  getAiCowbellFactorsHistory,
  getLatestAiCowbellFactors,
} from '../../console/customers/_services/dashboard.service';
import type { AiCowbellFactorsDto } from '../../types/cowbell-factors/ai-cowbell-factors.dto';

type ScoredAiFactorsDto = AiCowbellFactorsDto & { overall: number };

// Deltas compare the three factors only, so an earlier run needs nothing more.
const hasFactorScores = (value: unknown): value is AiCowbellFactorsDto =>
  AI_FACTOR_KEYS.every((key) =>
    Number.isFinite((value as AiCowbellFactorsDto | null)?.[key])
  );

const hasScores = (value: unknown): value is ScoredAiFactorsDto =>
  hasFactorScores(value) && Number.isFinite(value.overall);

export const useAiCowbellFactors = (accountId?: string) =>
  useQuery<ScoredAiFactorsDto | null>(
    ['ai-cowbell-factors', accountId],
    async () => {
      if (!accountId) {
        return null;
      }
      const response = await getLatestAiCowbellFactors(accountId);
      return hasScores(response?.data) ? response.data : null;
    },
    {
      enabled: Boolean(accountId),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

const getRunTime = (run: AiCowbellFactorsDto) =>
  moment.utc(run.generatedAt).valueOf();

const getPreviousRun = (
  history: readonly AiCowbellFactorsDto[],
  latest: AiCowbellFactorsDto
) =>
  history
    .filter((run) => getRunTime(run) < getRunTime(latest))
    .sort((older, newer) => getRunTime(newer) - getRunTime(older))[0] ?? null;

export const toAiFactorScores = (
  latest: ScoredAiFactorsDto,
  previous: AiCowbellFactorsDto | null
): AiFactorScores => {
  const factors = AI_FACTOR_KEYS.map((key) => ({
    key,
    score: Math.round(latest[key]),
    // Rounded per run so the delta always matches the two displayed scores.
    delta: previous
      ? Math.round(latest[key]) - Math.round(previous[key])
      : null,
  }));
  return {
    // The engine's customer score leans toward the weakest AI system; an
    // average of the three factors would let two good factors hide a bad one.
    postureIndex: Math.round(latest.overall),
    factors,
    sources: latest.sources,
  };
};

export const useAiCowbellFactorsHistory = (
  accountId?: string,
  params: Record<string, unknown> = {}
) =>
  useQuery<AiCowbellFactorsDto[]>(
    ['ai-cowbell-factors-history', accountId, params],
    async () => {
      if (!accountId) {
        return [];
      }
      const response = await getAiCowbellFactorsHistory(accountId, params);
      return Array.isArray(response?.data)
        ? response.data.filter(hasFactorScores)
        : [];
    },
    {
      enabled: Boolean(accountId),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

// The delta needs the run before the latest one, and only history carries it.
export const useAiFactorScores = (accountId?: string) => {
  const latestQuery = useAiCowbellFactors(accountId);
  const historyQuery = useAiCowbellFactorsHistory(accountId);
  const latest = latestQuery.data;
  const history = historyQuery.data ?? [];
  // Deltas come from history, so scores shown before it settles would all read
  // as a first-ever run. Only a run that exists is worth that wait: an account
  // with no AI factors resolves to nothing rather than flashing a skeleton.
  // A failed history query stops being loading, so the scores still render.
  const isAwaitingDeltas = Boolean(latest) && historyQuery.isInitialLoading;

  return {
    data: latest
      ? toAiFactorScores(latest, getPreviousRun(history, latest))
      : null,
    isLoading: latestQuery.isInitialLoading || isAwaitingDeltas,
  };
};
